import * as _ from 'lodash';
import { RRule } from 'rrule';
import moment from 'moment';
import { RRULE_FREQUENCY_MAP } from './constants';
import * as RRuleFunctions from '../../../../../HelperMethods/RRuleFunctions';
import * as DateUtils from '../../../../../HelperMethods/DateUtils';
import { EVENT_SOURCE } from '../../../../../HelperMethods/enums';

export const hasAssigneeAccepted = (enrichedTask, id, selectedttOriginalObject) => {
  const { taskType = {}, assigneeIds = [] } = enrichedTask;
  const { needAcceptance: selectedTTNeedAcceptance = false } = selectedttOriginalObject;
  const hasTaskTypeChanged = taskType?.id !== selectedttOriginalObject?.id;
  if (hasTaskTypeChanged && selectedTTNeedAcceptance) {
    return false;
  }
  const acceptedAssignee = assigneeIds.find((e) => !e.isDeleted && e.isAccepted && e.assigneeId === id);
  const { needAcceptance = false } = taskType;
  return needAcceptance && acceptedAssignee;
};
export function shouldAllowAcceptOrRejectBulkEdit(selectedTasks, taskTypeList) {
  if (selectedTasks) {
    const filteredTasks = selectedTasks.filter((e) => {
      const assigneeList = _.get(e, 'assigneeIds', []).filter((a) => !a.isDeleted);
      const taskTypeId = _.get(e, 'taskTypeId', '');
      const taskTypeObj = taskTypeList.filter((taskType) => taskType.id === taskTypeId);
      const { needAcceptance = false, needEquipment = false } = taskTypeObj[0];
      return (
        _.isArray(assigneeList) &&
        ((needEquipment && assigneeList.length >= 1) || (!needEquipment && assigneeList.length === 1)) &&
        needAcceptance
      );
    });
    return _.isArray(filteredTasks) && filteredTasks.length === selectedTasks.length;
  }
  return false;
}

export function shouldAllowAcceptOrReject(enrichedTask, formik, taskTypeOptions) {
  const { assigneeIds = [], taskTypeId } = enrichedTask;
  const filteredAssigneeIds = assigneeIds.filter((e) => !e.isDeleted);
  const taskType = taskTypeOptions.items.find((option) => option.value === _.get(formik, 'values.s_tasktype')) ?? {};
  const ttOriginalObject = _.get(taskType, 'originalobject', {}) || {};
  const { needAcceptance = false } = ttOriginalObject;
  const formAssigneeIds = (_.get(formik, 'values.s_assigneeid') || []).filter((val) => val);
  const taskAssigneeIds = enrichedTask.assigneeIds
    .filter((assignee) => !assignee.isDeleted)
    .map((assignee) => assignee.assigneeId);
  const diff = _.difference(formAssigneeIds, taskAssigneeIds);
  if (!needAcceptance) {
    return false;
  }
  if (diff && diff.length) {
    return true;
  }
  if (taskTypeId !== ttOriginalObject.id && assigneeIds?.length) {
    return true;
  }
  if (assigneeIds.length) {
    const acceptedAssignee = filteredAssigneeIds.filter((e) => e.isAccepted);
    return !acceptedAssignee.length;
  }
  return false;
}

export function isUserAddedOrInvalid(enrichedTask, formik, id) {
  if (!id) {
    return true;
  }
  const formAssigneeIds = (_.get(formik, 'values.s_assigneeid') || []).filter((val) => val);
  const taskAssigneeIds = enrichedTask.assigneeIds
    .filter((assignee) => !assignee.isDeleted)
    .map((assignee) => assignee.assigneeId);
  const diff = _.difference(formAssigneeIds, taskAssigneeIds);
  return diff.includes(id);
}

export function findErrorsToScrollInToView(validationResponse, isManifestFormValid) {
  const errorAttributeName = Object.keys(validationResponse || {});
  const taskDetailSections = [
    { attributeKey: 's_', attributeId: 'systemSegment' },
    { attributeKey: 'parentTaskTemplate', attributeId: 'parentTaskTemplate' },
    { attributeKey: 'schedule_', attributeId: 'scheduleSegment' },
    { attributeKey: 'parts_', attributeId: 'partsSegment' },
    { attributeKey: 'manifestForm', attributeId: 'manifestFormSegment' },
    { attributeKey: 'g_', attributeId: 'globalSegment' },
    { attributeKey: 'c_', attributeId: 'customSegment' },
    { attributeKey: 'watchergroupSchema_', attributeId: 'watchergroupSegment' },
  ];
  for (const { attributeKey, attributeId } of taskDetailSections) {
    if (attributeKey === 'manifestForm') {
      if (!isManifestFormValid) {
        const element = document.getElementById(attributeId);
        element.scrollIntoView();
        break;
      } else {
        continue;
      }
    }
    const elementFound = errorAttributeName.find((attributeName) => attributeName.startsWith(attributeKey));
    if (elementFound) {
      const element = document.getElementById(attributeId);
      element.scrollIntoView();
      break;
    }
  }
}

export const findExistingRule = (task) => {
  const rruleString = _.get(task, 'schedule.rrule');
  const overwriteEditedOccurrences = _.get(task, 'schedule.overwriteEditedOccurrences', true);
  const allowPileups = _.get(task, 'schedule.allowPileups', true);
  const displayUptoDays = _.get(task, 'schedule.displayUptoDays', 15);
  if (rruleString) {
    const rruleObj = RRule.parseString(rruleString);
    delete rruleObj.byhour;
    delete rruleObj.byminute;
    delete rruleObj.bysecond;
    delete rruleObj.wkst;
    const dateStartAtCurrentTZ = moment(rruleObj.dtstart).date();
    const dateStartAtUTC = moment(rruleObj.dtstart).utc().date();
    if (dateStartAtCurrentTZ > dateStartAtUTC) {
      if (rruleObj.freq === RRule.WEEKLY) {
        rruleObj.byweekday = rruleObj.byweekday.map((weekday) =>
          RRuleFunctions.getRRuleWeekDay(RRuleFunctions.getDayNoForRRuleWeekDay(weekday) + 1)
        );
      } else if (rruleObj.freq === RRule.MONTHLY) {
        rruleObj.bymonthday = (rruleObj.bymonthday % 30) + 1;
      }
    } else if (dateStartAtCurrentTZ < dateStartAtUTC) {
      if (rruleObj.freq === RRule.WEEKLY) {
        rruleObj.byweekday = rruleObj.byweekday.map((weekday) =>
          RRuleFunctions.getRRuleWeekDay(RRuleFunctions.getDayNoForRRuleWeekDay(weekday) + 6)
        );
      } else if (rruleObj.freq === RRule.MONTHLY) {
        rruleObj.bymonthday = ((rruleObj.bymonthday + 28) % 30) + 1;
      }
    }
    return {
      frequencytype: RRULE_FREQUENCY_MAP[rruleObj.freq],
      dailyinterval: rruleObj.freq === RRule.DAILY ? rruleObj.interval : 1,
      monthlyinterval: rruleObj.freq === RRule.MONTHLY ? rruleObj.interval : 1,
      byweekday: _.get(rruleObj, 'byweekday', []),
      bymonthday: rruleObj.bymonthday,
      startdate: DateUtils.convertDateToYYYYMMDDFormat(rruleObj.dtstart),
      enddate: DateUtils.convertDateToYYYYMMDDFormat(rruleObj.until),
      overwriteEditedOccurrences,
      allowPileups,
      displayUptoDays,
    };
  }
  return {};
};

export const changeRecurringRule = (formValues, selectedTasks) => {
  const reccuringRuleIsHidden = selectedTasks.some(
    (task) => task.eventSource !== EVENT_SOURCE.RECURRING || task.sourceRecurringTaskId
  );
  if (reccuringRuleIsHidden) {
    return;
  }
  for (const selectedTask of selectedTasks) {
    const existingRule = findExistingRule(selectedTask);
    const startDate = _.get(formValues, 'schedule_recurring.startdate') || existingRule.startdate;
    const endDate = _.get(formValues, 'schedule_recurring.enddate') || existingRule.enddate;
    const frequencyType = _.get(formValues, 'schedule_recurring.frequencytype') || existingRule.frequencytype;
    const displayUptoDays = _.get(formValues, 'schedule_recurring.displayUptoDays') || existingRule.displayUptoDays;
    const allowPileups = _.get(formValues, 'schedule_recurring.allowPileups') ?? existingRule.allowPileups;
    const overwriteEditedOccurrences =
      _.get(formValues, 'schedule_recurring.overwriteEditedOccurrences') ?? existingRule.overwriteEditedOccurrences;
    const dtstart = DateUtils.setTimeToDate(
      new Date(`${startDate}T00:00`),
      moment(selectedTask.startTime).format('HH:mm')
    );
    const dateStartAtCurrentTZ = moment(dtstart).date();
    const dateStartAtUTC = moment(dtstart).utc().date();
    let { byweekday } = existingRule;
    let { bymonthday } = existingRule;
    if (_.get(formValues, 'schedule_recurring.frequencytype')) {
      byweekday = _.get(formValues, 'schedule_recurring.byweekday', []);
      bymonthday = _.get(formValues, 'schedule_recurring.bymonthday', 1);
    }
    if (dateStartAtCurrentTZ > dateStartAtUTC) {
      if (frequencyType === RRULE_FREQUENCY_MAP[RRule.WEEKLY]) {
        byweekday = byweekday.map((weekday) =>
          RRuleFunctions.getRRuleWeekDay(RRuleFunctions.getDayNoForRRuleWeekDay(weekday) + 6)
        );
      } else if (frequencyType === RRULE_FREQUENCY_MAP[RRule.MONTHLY]) {
        bymonthday = ((parseInt(bymonthday) + 28) % 30) + 1;
      }
    } else if (dateStartAtCurrentTZ < dateStartAtUTC) {
      if (frequencyType === RRULE_FREQUENCY_MAP[RRule.WEEKLY]) {
        byweekday = byweekday.map((weekday) =>
          RRuleFunctions.getRRuleWeekDay(RRuleFunctions.getDayNoForRRuleWeekDay(weekday) + 1)
        );
      } else if (frequencyType === RRULE_FREQUENCY_MAP[RRule.MONTHLY]) {
        bymonthday = (parseInt(bymonthday) % 30) + 1;
      }
    } else {
      bymonthday = parseInt(bymonthday);
    }
    let interval = null;
    if (frequencyType === RRULE_FREQUENCY_MAP[RRule.DAILY]) {
      interval = _.get(formValues, 'schedule_recurring.frequencytype')
        ? _.get(formValues, 'schedule_recurring.dailyinterval', 1)
        : existingRule.dailyinterval;
    } else if (frequencyType === RRULE_FREQUENCY_MAP[RRule.MONTHLY]) {
      interval = _.get(formValues, 'schedule_recurring.frequencytype')
        ? _.get(formValues, 'schedule_recurring.monthlyinterval', 1)
        : existingRule.monthlyinterval;
    }
    const INVERTED_RRULE_FREQUENCY_MAP = _.invert(RRULE_FREQUENCY_MAP);
    const rrule = {
      dtstart,
      until: DateUtils.setTimeToDate(new Date(`${endDate}T00:00`), moment(selectedTask.startTime).format('HH:mm')),
      interval,
      byweekday: frequencyType === RRULE_FREQUENCY_MAP[RRule.WEEKLY] ? byweekday : null,
      bymonthday: frequencyType === RRULE_FREQUENCY_MAP[RRule.MONTHLY] ? bymonthday : null,
      freq: INVERTED_RRULE_FREQUENCY_MAP[frequencyType],
    };
    selectedTask.schedule = {
      rrule: RRule.optionsToString(new RRule(rrule).options),
      displayUptoDays,
      allowPileups,
      overwriteEditedOccurrences,
    };
  }
};
